import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trophy, TrendingDown, TrendingUp, Users, BarChart3, Activity, Zap, Clock, Award, X, Star, Sparkles, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import BestTimesView from './BestTimesView';
import LapByLapView from './LapByLapView';
import LapByLapComparison from './LapByLapComparison';
import ConsistencyView from './ConsistencyView';
import ProvisionalResults from './ProvisionalResults';
import SessionReport from './SessionReport';
import SessionOverview from './SessionOverview';
import SectorHeatmapView from './SectorHeatmapView';
import { Flame } from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import { API } from '../config/api';

const COLORS = ['#dc2626', '#ea580c', '#f59e0b', '#eab308', '#84cc16', '#22c55e'];

const RaceComparison = ({ race, isPro = false, userId }) => {
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [categoryBestTimes, setCategoryBestTimes] = useState(null);
  const [selectedDriverLaps, setSelectedDriverLaps] = useState(null);
  const [consistencyData, setConsistencyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('comparison');
  const [favoriteDrivers, setFavoriteDrivers] = useState(new Set());
  // New state for lap selection per driver
  const [selectedLaps, setSelectedLaps] = useState({});  // { driverNumber: lapNumber | 'best' }
  // Sector source toggle — pedagogical clarifier added Feb 2026.
  //   'real'    = sectors AS RUN in each driver's fast lap (a single physical lap)
  //   'optimal' = each driver's best sector across the whole session (potentially
  //               different laps glued together — a theoretical best)
  // Default 'optimal' to preserve previous behaviour.
  const [sectorMode, setSectorMode] = useState('optimal');

  useEffect(() => {
    // Reset all state when race changes
    setSelectedDrivers([]);
    setComparisonData(null);
    setCategoryBestTimes(null);
    setSelectedDriverLaps(null);
    setViewMode('comparison');
    setSelectedLaps({});  // Reset lap selections
    
    // Get unique drivers (filter duplicates)
    const uniqueDrivers = race.drivers.filter((driver, index, self) => 
      index === self.findIndex(d => d.driver_number === driver.driver_number)
    );
    
    // Auto-select first 2 unique drivers
    if (uniqueDrivers.length >= 2) {
      const first = uniqueDrivers[0].driver_number;
      const second = uniqueDrivers[1].driver_number;
      setSelectedDrivers([first, second]);
    }
    
    // Load favorites
    if (userId) {
      loadFavorites();
    }
  }, [race.id, userId]);

  const loadFavorites = async () => {
    if (!userId) return;
    try {
      const response = await axios.get(`${API}/favorites?user_id=${userId}`);
      const favNames = new Set(response.data.map(f => f.driver_name.toLowerCase()));
      setFavoriteDrivers(favNames);
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  const toggleFavorite = async (driverName) => {
    if (!userId) {
      toast.error('Inicia sesión para marcar favoritos');
      return;
    }
    
    const isCurrentlyFavorite = favoriteDrivers.has(driverName.toLowerCase());
    
    try {
      if (isCurrentlyFavorite) {
        await axios.delete(`${API}/favorites/by-name/${encodeURIComponent(driverName)}?user_id=${userId}`);
        setFavoriteDrivers(prev => {
          const newSet = new Set(prev);
          newSet.delete(driverName.toLowerCase());
          return newSet;
        });
        toast.success(`${driverName} quitado de favoritos`);
      } else {
        await axios.post(`${API}/favorites?user_id=${userId}`, {
          driver_name: driverName,
          category: race.category || 'turismo_pista'
        });
        setFavoriteDrivers(prev => new Set([...prev, driverName.toLowerCase()]));
        toast.success(`${driverName} agregado a favoritos ⭐`);
      }
    } catch (error) {
      toast.error('Error al actualizar favoritos');
    }
  };

  const handleDriverSelect = (driverNumber) => {
    setSelectedDrivers(prev => {
      if (prev.includes(driverNumber)) {
        return prev.filter(n => n !== driverNumber);
      } else {
        return [...prev, driverNumber];
      }
    });
  };

  // Handle lap selection change for a driver
  const handleLapSelection = (driverNumber, lapValue) => {
    setSelectedLaps(prev => ({
      ...prev,
      [driverNumber]: lapValue
    }));
  };

  // Get all laps for a driver from race data
  const getDriverLaps = (driverNumber) => {
    const driver = race.drivers.find(d => d.driver_number === driverNumber);
    if (!driver || !driver.lap_details) return [];
    return driver.lap_details;
  };

  // Get the selected lap data for comparison (returns sector times for specific lap)
  const getSelectedLapData = (driverNumber) => {
    const driver = race.drivers.find(d => d.driver_number === driverNumber);
    if (!driver) return null;
    
    const selectedLap = selectedLaps[driverNumber] || 'best';
    
    if (selectedLap === 'best' || !driver.lap_details) {
      // Return best lap data (default behavior)
      return {
        lap_number: 'Mejor',
        lap_time: driver.best_lap,
        sector_times: driver.sector_times?.map(st => Math.min(...st.times)) || []
      };
    }
    
    // Find specific lap
    const lapData = driver.lap_details.find(l => l.lap_number === parseInt(selectedLap));
    if (!lapData) return null;
    
    return {
      lap_number: lapData.lap_number,
      lap_time: lapData.lap_time,
      sector_times: lapData.sector_times || []
    };
  };

  const handleCompare = async () => {
    if (selectedDrivers.length < 2) {
      toast.error('Selecciona al menos 2 pilotos para comparar');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/races/${race.id}/compare`, {
        driver_numbers: selectedDrivers
      });
      setComparisonData(response.data);
      setViewMode('comparison');
      toast.success('Comparación generada');
    } catch (error) {
      toast.error('Error al generar la comparación');
    } finally {
      setLoading(false);
    }
  };

  // Get color based on speed (fastest = white, 2nd = yellow, slowest = red)
  const getDriverColor = (driverIndex) => {
    if (!comparisonData || comparisonData.drivers.length < 2) return COLORS[driverIndex % COLORS.length];
    
    const drivers = [...comparisonData.drivers].sort((a, b) => {
      if (a.best_lap <= 0 && b.best_lap > 0) return 1;
      if (b.best_lap <= 0 && a.best_lap > 0) return -1;
      return a.best_lap - b.best_lap;
    });
    const currentDriver = comparisonData.drivers[driverIndex];
    const speedRank = drivers.findIndex(d => d.driver_number === currentDriver.driver_number);
    
    // For 3+ drivers: white (fastest), yellow (middle), red (slowest)
    if (drivers.length === 3) {
      if (speedRank === 0) return '#ffffff'; // Fastest - white
      if (speedRank === 1) return '#fbbf24'; // Middle - yellow
      if (speedRank === 2) return '#dc2626'; // Slowest - red
    }
    
    // For 2 drivers or more than 3
    const ratio = drivers.length > 1 ? speedRank / (drivers.length - 1) : 0;
    
    if (ratio === 0) return '#ffffff'; // Fastest - white
    if (ratio === 1) return '#dc2626'; // Slowest - red
    
    // Interpolate: white -> yellow -> red
    if (ratio < 0.5) {
      // White to yellow
      const localRatio = ratio * 2;
      const r = 255;
      const g = 255 - Math.round(64 * localRatio); // 255 -> 191
      const b = 255 - Math.round(219 * localRatio); // 255 -> 36
      return `rgb(${r}, ${g}, ${b})`;
    } else {
      // Yellow to red
      const localRatio = (ratio - 0.5) * 2;
      const r = 251 - Math.round(31 * localRatio); // 251 -> 220
      const g = 191 - Math.round(153 * localRatio); // 191 -> 38
      const b = 36 - Math.round(0 * localRatio); // 36 -> 36
      return `rgb(${r}, ${g}, ${b})`;
    }
  };

  const loadBestTimes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/races/${race.id}/best-times`);
      setCategoryBestTimes(response.data);
      setViewMode('best-times');
    } catch (error) {
      toast.error('Error al cargar mejores tiempos');
    } finally {
      setLoading(false);
    }
  };

  const loadDriverLaps = async (driverNumber) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/races/${race.id}/drivers/${driverNumber}/laps`);
      setSelectedDriverLaps(response.data);
      setViewMode('laps');
    } catch (error) {
      toast.error('Error al cargar vueltas del piloto');
    } finally {
      setLoading(false);
    }
  };


  const loadConsistencyAnalysis = async () => {
    if (selectedDrivers.length < 1) {
      toast.error('Selecciona al menos un piloto');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/races/${race.id}/consistency`, {
        driver_numbers: selectedDrivers
      });
      setConsistencyData(response.data);
      setViewMode('consistency');
    } catch (error) {
      toast.error('Error al cargar análisis de consistencia');
    } finally {
      setLoading(false);
    }
  };


  const handleDeleteDriver = async (driverNumber, driverName) => {
    if (!window.confirm(`¿Eliminar piloto #${driverNumber} ${driverName}? Esta acción no se puede deshacer.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/races/${race.id}/drivers/${driverNumber}`);
      toast.success(`Piloto #${driverNumber} eliminado`);
      
      // Update local race object to reflect deletion
      race.drivers = race.drivers.filter(d => d.driver_number !== driverNumber);
      
      // Remove from selected if it was selected
      setSelectedDrivers(prev => prev.filter(n => n !== driverNumber));
      
      // Clear comparison data if deleted driver was in comparison
      if (comparisonData && comparisonData.drivers.some(d => d.driver_number === driverNumber)) {
        setComparisonData(null);
        setViewMode('comparison');
      }
      
      // Force re-render by updating the key
      window.location.reload();
    } catch (error) {
      toast.error('Error al eliminar piloto');
      console.error('Delete driver error:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(3);
    return mins > 0 ? `${mins}:${secs.padStart(6, '0')}` : `${secs}s`;
  };

  // Calculate Y-axis domain for better visualization
  const getYAxisDomain = (data, keys) => {
    if (!data || data.length === 0) return ['auto', 'auto'];
    
    let allValues = [];
    data.forEach(point => {
      keys.forEach(key => {
        if (point[key] && point[key] > 0) {
          allValues.push(point[key]);
        }
      });
    });
    
    if (allValues.length === 0) return ['auto', 'auto'];
    
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const range = max - min;
    
    // Use very tight range (5% padding) to maximize contrast
    const padding = range * 0.05;
    return [
      Math.max(0, min - padding),
      max + padding
    ];
  };

  // Helper — return the sector array for a driver based on current sectorMode.
  // 'optimal' (default): driver.sector_bests (per-sector best across the session).
  // 'real':              driver.best_lap_sectors (the sectors as run in the fast lap).
  //                      Falls back to sector_bests when the backend didn't send
  //                      best_lap_sectors (old data or no lap_details).
  const sectorsOf = (driver) => {
    if (sectorMode === 'real' && Array.isArray(driver.best_lap_sectors) && driver.best_lap_sectors.length) {
      return driver.best_lap_sectors;
    }
    return driver.sector_bests || [];
  };

  // Prepare chart data - Use best sector times instead of averages
  const getSectorChartData = () => {
    if (!comparisonData) return [];

    const data = [];
    const maxSectors = Math.max(...comparisonData.drivers.map(d => sectorsOf(d).length));

    for (let i = 0; i < maxSectors; i++) {
      const point = { sector: `S${i + 1}` };
      comparisonData.drivers.forEach((driver) => {
        // Use sector_bests to show the best time per sector (not average)
        point[driver.driver_name] = sectorsOf(driver)[i] || 0;
      });
      data.push(point);
    }

    return data;
  };

  const getLapTimeChartData = () => {
    if (!comparisonData) return [];

    return comparisonData.drivers.map(driver => ({
      name: driver.driver_name,
      'Mejor Vuelta': driver.best_lap,
      'Promedio': driver.avg_lap
    }));
  };

  const getRadarChartData = () => {
    if (!comparisonData) return [];

    const data = [];
    const maxSectors = Math.max(...comparisonData.drivers.map(d => d.sector_bests.length));

    for (let i = 0; i < maxSectors; i++) {
      const point = { sector: `S${i + 1}` };
      
      // Get all best times for this sector (not averages)
      const sectorTimes = comparisonData.drivers.map(d => d.sector_bests[i] || 0).filter(t => t > 0);
      if (sectorTimes.length === 0) continue;
      
      const minTime = Math.min(...sectorTimes);
      const maxTime = Math.max(...sectorTimes);
      const range = maxTime - minTime;
      
      comparisonData.drivers.forEach((driver) => {
        const time = driver.sector_bests[i] || 0;
        // Normalize to 0-100 scale (faster = higher value)
        // Add baseline of 50 to keep values visible
        if (time > 0 && range > 0) {
          point[driver.driver_name] = 50 + ((maxTime - time) / range) * 50;
        } else {
          point[driver.driver_name] = 50;
        }
      });
      data.push(point);
    }

    return data;
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Race Header */}
      <Card className="bg-neutral-900 border-red-900/30 backdrop-blur">
        <CardHeader className="p-3 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 min-w-0">
            <div className="min-w-0 flex-1">
              <CardTitle className="text-lg md:text-2xl text-white flex items-center gap-2 min-w-0">
                <Trophy className="w-5 h-5 md:w-6 md:h-6 text-red-400 flex-shrink-0" />
                <span className="truncate min-w-0">{race.name}</span>
              </CardTitle>
              <CardDescription className="text-gray-400 mt-1 md:mt-2 text-sm">
                {new Date(race.date).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </CardDescription>
            </div>
            <Badge className="bg-red-600 text-white self-start">
              {race.drivers.length} pilotos
            </Badge>
          </div>
          {/* View-only banner: this session has no lap-by-lap data (grilla /
              plantilla general). Comparativa por sector, óptimos y heatmap
              no aplican. Rendered as a soft-warning to avoid surprising the
              user when the tabs are empty. */}
          {!race.drivers.some(d => Array.isArray(d.lap_details) && d.lap_details.length > 0) && (
            <div
              className="mt-3 rounded-md border border-sky-800/40 bg-sky-950/25 px-3 py-2 flex items-start gap-2 text-xs md:text-sm text-sky-200"
              data-testid="viewonly-banner"
            >
              <Eye className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold text-sky-100">Modo visualización</div>
                Este archivo <b>no contiene datos vuelta por vuelta</b> (parece una plantilla o grilla general).
                Podés ver posiciones y tiempos totales, pero la comparativa por parciales, la vuelta óptima y el heatmap no están disponibles.
              </div>
            </div>
          )}
        </CardHeader>
      </Card>

      <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
        {/* Driver Selection */}
        <Card className="bg-neutral-900 border-red-900/30 lg:col-span-1">
          <CardHeader className="p-3 md:p-6 pb-2 md:pb-2">
            <CardTitle className="text-base md:text-lg text-white flex items-center gap-2">
              <Users className="w-4 h-4 md:w-5 md:h-5 text-red-500" />
              Seleccionar Pilotos
            </CardTitle>
            <CardDescription className="text-gray-400 text-sm">
              Selecciona 2 o más pilotos para comparar
            </CardDescription>
          </CardHeader>
          <CardContent className="p-3 md:p-6 pt-0">
            <ScrollArea className="h-[300px] md:h-[500px] pr-2 md:pr-4">
              <div className="space-y-3">
                {race.drivers
                  .filter((driver, index, self) => 
                    // Remove duplicates based on driver_number
                    index === self.findIndex(d => d.driver_number === driver.driver_number)
                  )
                  .sort((a, b) => {
                    // Sort drivers with best_lap=0 (no valid laps) to the end
                    if (a.best_lap <= 0 && b.best_lap > 0) return 1;
                    if (b.best_lap <= 0 && a.best_lap > 0) return -1;
                    return a.best_lap - b.best_lap;
                  })  // Sort by best lap
                  .map((driver, index, sortedDrivers) => {
                  const leaderTime = sortedDrivers[0]?.best_lap || 0;
                  const gapToLeader = driver.best_lap - leaderTime;
                  const position = index + 1;
                  
                  return (
                  <div
                    key={`driver-${driver.driver_number}-${driver.driver_name}`}
                    className={`flex items-center space-x-3 p-3 rounded-lg border transition-all cursor-pointer ${
                      selectedDrivers.includes(driver.driver_number)
                        ? 'bg-red-600/20 border-red-600'
                        : 'bg-neutral-800/30 border-red-900/30 hover:border-red-900/50'
                    }`}
                    onClick={() => handleDriverSelect(driver.driver_number)}
                    data-testid={`driver-checkbox-${driver.driver_number}`}
                  >
                    {/* Position Badge */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      position === 1 ? 'bg-yellow-500 text-black' :
                      position === 2 ? 'bg-gray-300 text-black' :
                      position === 3 ? 'bg-amber-600 text-white' :
                      'bg-neutral-700 text-gray-300'
                    }`}>
                      {position}
                    </div>
                    
                    <Checkbox
                      checked={selectedDrivers.includes(driver.driver_number)}
                      onCheckedChange={() => handleDriverSelect(driver.driver_number)}
                      id={`driver-${driver.driver_number}`}
                    />
                    <Label
                      htmlFor={`driver-${driver.driver_number}`}
                      className="flex-1 cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-white">#{driver.driver_number}</p>
                          <p className="text-sm text-gray-400">{driver.driver_name}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            {/* Best-lap badge — purple for the session leader
                                ("mejor de la tanda"), green for everyone else
                                whose value IS their own personal best. */}
                            <Badge
                              data-testid={`best-lap-badge-${driver.driver_number}`}
                              className={
                                position === 1 && driver.best_lap > 0
                                  ? 'bg-purple-600 text-white border-purple-500 font-mono'
                                  : driver.best_lap > 0
                                  ? 'bg-green-700/40 text-green-300 border border-green-700/60 font-mono'
                                  : 'bg-neutral-800 text-gray-500 border border-neutral-700 font-mono'
                              }
                            >
                              {formatTime(driver.best_lap)}
                            </Badge>
                            {gapToLeader > 0 && (
                              <p className="text-xs text-red-400 mt-1">
                                +{gapToLeader.toFixed(3)}s
                              </p>
                            )}
                          </div>
                          {/* Favorite Button */}
                          <Button
                            size="sm"
                            variant="ghost"
                            className={`h-8 px-2 ${
                              favoriteDrivers.has(driver.driver_name.toLowerCase())
                                ? 'text-yellow-500 hover:text-yellow-400'
                                : 'text-gray-500 hover:text-yellow-500'
                            } hover:bg-yellow-950/30`}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(driver.driver_name);
                            }}
                            data-testid={`favorite-${driver.driver_number}`}
                            title={favoriteDrivers.has(driver.driver_name.toLowerCase()) ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                          >
                            <Star className={`w-4 h-4 ${favoriteDrivers.has(driver.driver_name.toLowerCase()) ? 'fill-yellow-500' : ''}`} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2 text-red-400 hover:text-red-300 hover:bg-red-950/30"
                            onClick={(e) => {
                              e.stopPropagation();
                              loadDriverLaps(driver.driver_number);
                            }}
                            data-testid={`view-laps-${driver.driver_number}`}
                          >
                            <Clock className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2 text-red-500 hover:text-red-400 hover:bg-red-950/50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteDriver(driver.driver_number, driver.driver_name);
                            }}
                            data-testid={`delete-driver-${driver.driver_number}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Label>
                  </div>
                  );
                })}
              </div>
            </ScrollArea>

            <Separator className="my-3 md:my-4 bg-neutral-700" />

            <div className="space-y-2">
              <Button
                onClick={handleCompare}
                disabled={selectedDrivers.length < 2 || loading}
                className="w-full bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/30 h-9 md:h-10 text-sm"
                data-testid="compare-button"
              >
                {loading ? (
                  'Analizando...'
                ) : (
                  <>
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Comparar ({selectedDrivers.length})
                  </>
                )}
              </Button>
              
              {/* Action buttons - 2 column grid on mobile */}
              <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
                <Button
                  onClick={() => {
                    if (!isPro) {
                      toast.error('🔒 Función exclusiva del plan PRO');
                      return;
                    }
                    loadBestTimes();
                  }}
                  disabled={loading}
                  className="w-full bg-neutral-800 hover:bg-neutral-700 text-white border border-red-600/50 hover:border-red-600 h-8 md:h-10 text-xs md:text-sm px-2"
                  data-testid="best-times-button"
                >
                  {!isPro && <span className="mr-1">🔒</span>}
                  <Award className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                  <span className="truncate">Parciales</span>
                </Button>

                <Button
                  onClick={() => {
                    if (!isPro) {
                      toast.error('🔒 Función exclusiva del plan PRO');
                      return;
                    }
                    setViewMode('sector-heatmap');
                  }}
                  disabled={loading}
                  className="w-full bg-neutral-800 hover:bg-neutral-700 text-white border border-purple-700/50 hover:border-purple-700 h-8 md:h-10 text-xs md:text-sm px-2"
                  data-testid="heatmap-button"
                >
                  {!isPro && <span className="mr-1">🔒</span>}
                  <Flame className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2 text-purple-400" />
                  <span className="truncate">Heatmap + Predicción</span>
                </Button>
                
                <Button
                  onClick={() => setViewMode('laps-comparison')}
                  disabled={selectedDrivers.length < 2}
                  className="w-full bg-neutral-800 hover:bg-neutral-700 text-white border border-red-600/50 hover:border-red-600 h-8 md:h-10 text-xs md:text-sm px-2"
                  data-testid="laps-comparison-button"
                >
                  <Clock className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                  <span className="truncate">Vuelta x Vuelta</span>
                </Button>
                
                <Button
                  onClick={() => {
                    if (!isPro) {
                      toast.error('🔒 Función exclusiva del plan PRO');
                      return;
                    }
                    loadConsistencyAnalysis();
                  }}
                  disabled={selectedDrivers.length < 1 || loading}
                  className="w-full bg-neutral-800 hover:bg-neutral-700 text-white border border-red-600/50 hover:border-red-600 h-8 md:h-10 text-xs md:text-sm px-2"
                  data-testid="consistency-button"
                >
                  {!isPro && <span className="mr-1">🔒</span>}
                  <Activity className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                  <span className="truncate">Consistencia</span>
                </Button>

                <Button
                  onClick={() => setViewMode('provisional-results')}
                  className="w-full bg-neutral-800 hover:bg-neutral-700 text-white border border-red-600/50 hover:border-red-600 h-8 md:h-10 text-xs md:text-sm px-2 col-span-2 md:col-span-1"
                  data-testid="provisional-results-button"
                >
                  <Trophy className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                  <span className="truncate">Resultados Provisorios</span>
                </Button>

                <Button
                  onClick={() => {
                    if (!isPro) {
                      toast.error('🔒 Función exclusiva del plan PRO');
                      return;
                    }
                    setViewMode('session-report');
                  }}
                  className="w-full bg-neutral-800 hover:bg-neutral-700 text-white border border-red-600/50 hover:border-red-600 h-8 md:h-10 text-xs md:text-sm px-2 col-span-2 md:col-span-1"
                  data-testid="session-report-button"
                >
                  {!isPro && <span className="mr-1">🔒</span>}
                  <Sparkles className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                  <span className="truncate">Reporte</span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Area with Tabs */}
        <div className="lg:col-span-2 space-y-6">
          {viewMode === 'best-times' ? (
            <BestTimesView 
              raceId={race.id} 
              comparisonData={comparisonData}
              getDriverColor={getDriverColor}
              race={race}
              userId={userId}
            />
          ) : viewMode === 'sector-heatmap' ? (
            <SectorHeatmapView race={race} selectedDrivers={selectedDrivers} />
          ) : viewMode === 'laps' && selectedDriverLaps ? (
            <LapByLapView raceId={race.id} driverNumber={selectedDriverLaps.driver_number} />
          ) : viewMode === 'laps-comparison' ? (
            <LapByLapComparison 
              raceId={race.id} 
              driverNumbers={selectedDrivers}
              getDriverColor={getDriverColor}
            />
          ) : viewMode === 'consistency' && consistencyData ? (
            <ConsistencyView 
              consistencyData={consistencyData}
              getDriverColor={getDriverColor}
            />
          ) : viewMode === 'session-report' ? (
            <SessionReport raceId={race.id} />
          ) : viewMode === 'provisional-results' ? (
            <ProvisionalResults race={race} />
          ) : !comparisonData ? (
            <SessionOverview race={race} />
          ) : (
            <>
              {/* Summary Cards with Lap Selector and Deltas */}
              <div className="grid md:grid-cols-2 gap-4">
                {comparisonData.drivers.map((driver, idx) => {
                  // Calculate delta vs fastest driver
                  const fastestDriver = [...comparisonData.drivers].filter(d => d.best_lap > 0).sort((a, b) => a.best_lap - b.best_lap)[0];
                  const deltaVsFastest = driver.best_lap - fastestDriver.best_lap;
                  const isFastest = deltaVsFastest === 0;
                  
                  // Get laps for this driver
                  const driverLaps = getDriverLaps(driver.driver_number);
                  const selectedLap = selectedLaps[driver.driver_number] || 'best';
                  const selectedLapData = getSelectedLapData(driver.driver_number);
                  
                  return (
                  <Card
                    key={driver.driver_number}
                    className="bg-neutral-900 border-red-900/30"
                    style={{ borderColor: getDriverColor(idx) }}
                    data-testid={`driver-summary-${driver.driver_number}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg text-white flex items-center gap-2">
                            #{driver.driver_number}
                            {isFastest && <Trophy className="w-4 h-4 text-yellow-500" />}
                          </CardTitle>
                          <CardDescription className="text-gray-300">
                            {driver.driver_name}
                          </CardDescription>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: getDriverColor(idx) }}
                          />
                          {!isFastest && (
                            <Badge className="bg-red-900/50 text-red-300 text-xs">
                              +{deltaVsFastest.toFixed(3)}s
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {/* Lap Selector */}
                        {driverLaps.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs text-gray-500 mb-1">Analizar vuelta:</p>
                            <Select
                              value={selectedLap.toString()}
                              onValueChange={(val) => handleLapSelection(driver.driver_number, val)}
                            >
                              <SelectTrigger className="h-8 bg-neutral-800 border-neutral-700 text-sm text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-neutral-800 border-neutral-700">
                                <SelectItem value="best" className="text-white hover:bg-neutral-700 focus:bg-neutral-700 focus:text-white">🏆 Mejor Vuelta</SelectItem>
                                {driverLaps.map(lap => {
                                  // Classify lap: fast (within 105% of best), slow (>110%), or normal
                                  const bestLapTime = Math.min(...driverLaps.map(l => l.lap_time));
                                  const lapRatio = lap.lap_time / bestLapTime;
                                  const isSlow = lapRatio > 1.10; // More than 10% slower
                                  const isFast = lapRatio <= 1.05; // Within 5% of best
                                  
                                  return (
                                    <SelectItem 
                                      key={lap.lap_number} 
                                      value={lap.lap_number.toString()}
                                      className="text-white hover:bg-neutral-700 focus:bg-neutral-700 focus:text-white"
                                    >
                                      <span className="flex items-center gap-2">
                                        {isSlow ? '🐢' : isFast ? '🏎️' : '⚪'} 
                                        Vuelta {lap.lap_number} - {formatTime(lap.lap_time)}
                                        {isSlow && <span className="text-yellow-500 text-xs">(lenta)</span>}
                                      </span>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        
                        <div>
                          <p className="text-sm text-gray-400 flex items-center gap-1.5">
                            {selectedLap === 'best' ? 'Mejor Vuelta' : `Vuelta ${selectedLap}`}
                            {selectedLap === 'best' && (
                              <Badge
                                className="bg-blue-700/30 text-blue-300 border-blue-600/50 text-[9px] px-1.5 py-0"
                                title="Tiempo REAL: una vuelta física dada en pista (no es la suma de mejores parciales)"
                              >
                                REAL
                              </Badge>
                            )}
                          </p>
                          <div className="flex items-baseline gap-2">
                            <p className="text-2xl font-bold text-white">
                              {formatTime(selectedLapData?.lap_time || driver.best_lap)}
                            </p>
                            {!isFastest && selectedLap === 'best' && (
                              <span className="text-sm text-red-400">
                                (+{deltaVsFastest.toFixed(3)})
                              </span>
                            )}
                            {selectedLap === 'best' && driver.best_lap_number != null && (
                              <span
                                className="text-[10px] text-blue-400 font-mono"
                                title={`Vuelta #${driver.best_lap_number} de la sesión`}
                              >
                                L{driver.best_lap_number}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-400 flex items-center gap-1.5">
                            Vuelta Óptima
                            <Badge
                              className="bg-violet-700/30 text-violet-300 border-violet-600/50 text-[9px] px-1.5 py-0"
                              title="Tiempo TEÓRICO: suma de los mejores parciales individuales (pueden venir de vueltas distintas — no es una vuelta física dada)"
                            >
                              ÓPTIMO
                            </Badge>
                            {/* Real vs theoretical flag: when all sector bests came
                                from the same physical lap, the optimal == best_lap
                                and the driver actually achieved their potential. */}
                            {(() => {
                              const bls = driver.sector_best_laps;
                              if (Array.isArray(bls) && bls.length > 0 && bls.every(l => l && l === bls[0])) {
                                return (
                                  <Badge
                                    className="bg-emerald-700/30 text-emerald-300 border-emerald-600/50 text-[9px] px-1.5 py-0"
                                    title="Sus 3 mejores parciales se dieron en la misma vuelta — su óptimo es REAL, lo concretó"
                                    data-testid={`optimum-achieved-${driver.driver_number}`}
                                  >
                                    ✓ Concretada
                                  </Badge>
                                );
                              }
                              return null;
                            })()}
                          </p>
                          <div className="flex items-baseline gap-2">
                            <p className="text-xl font-semibold text-red-400">
                              {formatTime(driver.optimal_lap)}
                            </p>
                            {driver.optimal_lap < driver.best_lap && (
                              <span className="text-xs text-green-400" title="Margen de mejora vs su mejor vuelta real">
                                (-{(driver.best_lap - driver.optimal_lap).toFixed(3)})
                              </span>
                            )}
                          </div>
                          {/* Breakdown of which laps the best sectors came from */}
                          {Array.isArray(driver.sector_best_laps) && driver.sector_best_laps.some(l => l) && (
                            <p
                              className="text-[10px] text-gray-500 mt-0.5 font-mono"
                              title="Vueltas de origen de cada parcial best"
                            >
                              {driver.sector_best_laps.map((l, i) => `S${i + 1}:L${l || '?'}`).join(' · ')}
                            </p>
                          )}
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-400">Promedio</p>
                          <p className="text-lg font-medium text-gray-300">
                            {formatTime(driver.avg_lap)}
                          </p>
                        </div>
                        
                        {/* Lap classification summary */}
                        {driverLaps.length > 0 && (() => {
                          const bestLapTime = Math.min(...driverLaps.map(l => l.lap_time));
                          const fastLaps = driverLaps.filter(l => l.lap_time / bestLapTime <= 1.05);
                          const normalLaps = driverLaps.filter(l => l.lap_time / bestLapTime > 1.05 && l.lap_time / bestLapTime <= 1.10);
                          const slowLaps = driverLaps.filter(l => l.lap_time / bestLapTime > 1.10);
                          const realLaps = fastLaps.length + normalLaps.length; // Vueltas "reales" en pista
                          
                          return (
                            <div className="pt-2 border-t border-neutral-700">
                              <p className="text-xs text-gray-500 mb-2">Vueltas en pista:</p>
                              <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="text-center p-1 bg-green-900/20 rounded">
                                  <p className="text-green-400 font-bold">{fastLaps.length}</p>
                                  <p className="text-gray-500">🏎️ Rápidas</p>
                                </div>
                                <div className="text-center p-1 bg-neutral-800 rounded">
                                  <p className="text-gray-300 font-bold">{normalLaps.length}</p>
                                  <p className="text-gray-500">⚪ Normales</p>
                                </div>
                                <div className="text-center p-1 bg-yellow-900/20 rounded">
                                  <p className="text-yellow-400 font-bold">{slowLaps.length}</p>
                                  <p className="text-gray-500">🐢 Lentas</p>
                                </div>
                              </div>
                              <p className="text-xs text-gray-400 mt-2 text-center">
                                <span className="text-white font-bold">{realLaps}</span> vueltas lanzadas de <span className="text-white">{driverLaps.length}</span> totales
                              </p>
                            </div>
                          );
                        })()}
                        
                        {/* Sector times for selected lap */}
                        {selectedLapData?.sector_times && selectedLapData.sector_times.length > 0 && (
                          <div className="pt-2 border-t border-neutral-700">
                            <p className="text-xs text-gray-500 mb-2">Parciales:</p>
                            <div className="grid grid-cols-5 gap-1 text-xs">
                              {selectedLapData.sector_times.map((time, sIdx) => {
                                // Find fastest sector time among all drivers for this sector
                                const allSectorTimes = comparisonData.drivers.map(d => d.sector_bests[sIdx]).filter(t => t > 0);
                                const fastestSector = allSectorTimes.length > 0 ? Math.min(...allSectorTimes) : time;
                                const sectorDelta = time - fastestSector;
                                const isFastestSector = sectorDelta < 0.001;
                                
                                return (
                                  <div key={sIdx} className={`text-center p-1 rounded ${isFastestSector ? 'bg-green-900/30' : 'bg-neutral-800'}`}>
                                    <p className="text-gray-500">S{sIdx + 1}</p>
                                    <p className={`font-mono ${isFastestSector ? 'text-green-400' : 'text-gray-300'}`}>
                                      {time.toFixed(2)}
                                    </p>
                                    {!isFastestSector && sectorDelta > 0 && (
                                      <p className="text-red-400 text-[10px]">+{sectorDelta.toFixed(2)}</p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>

              {/* Lap Times Comparison */}
              <Card className="bg-neutral-900 border-red-900/30">
                <CardHeader>
                  <CardTitle className="text-white">Comparación de Tiempos de Vuelta</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={getLapTimeChartData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                      <XAxis dataKey="name" stroke="#9ca3af" />
                      <YAxis stroke="#9ca3af" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#171717',
                          border: '1px solid #7f1d1d',
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: '#ffffff' }}
                      />
                      <Legend />
                      <Bar dataKey="Mejor Vuelta" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                      <Bar dataKey="Promedio" fill="#f97316" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Sector Times Line Chart with Deltas */}
              <Card className="bg-neutral-900 border-red-900/30">
                <CardHeader>
                  <CardTitle className="text-white flex items-center justify-between gap-3 flex-wrap">
                    <span>
                      {sectorMode === 'real' ? 'Sectores de la Vuelta Rápida Real' : 'Mejores Tiempos por Sector'}
                    </span>
                    {/* Toggle real / optimal */}
                    <div
                      className="inline-flex rounded-md border border-neutral-700 overflow-hidden text-xs"
                      data-testid="sector-mode-toggle"
                    >
                      <button
                        type="button"
                        onClick={() => setSectorMode('real')}
                        data-testid="sector-mode-real"
                        title="Mostrar los parciales TAL CUAL se dieron en la vuelta rápida física de cada piloto"
                        className={`px-2.5 py-1 transition ${
                          sectorMode === 'real'
                            ? 'bg-blue-600 text-white font-medium'
                            : 'bg-neutral-800 text-gray-400 hover:text-white'
                        }`}
                      >
                        Vuelta rápida REAL
                      </button>
                      <button
                        type="button"
                        onClick={() => setSectorMode('optimal')}
                        data-testid="sector-mode-optimal"
                        title="Mostrar el mejor parcial INDIVIDUAL de cada piloto en cada sector (potencialmente de vueltas distintas)"
                        className={`px-2.5 py-1 transition ${
                          sectorMode === 'optimal'
                            ? 'bg-violet-600 text-white font-medium'
                            : 'bg-neutral-800 text-gray-400 hover:text-white'
                        }`}
                      >
                        Vuelta ÓPTIMA
                      </button>
                    </div>
                  </CardTitle>
                  <CardDescription className="text-gray-400 text-xs leading-relaxed">
                    {sectorMode === 'real' ? (
                      <>
                        <Badge className="bg-blue-700/30 text-blue-300 border-blue-600/50 text-[10px] mr-1.5 align-middle">REAL</Badge>
                        Los S1/S2/S3 que cada piloto hizo <span className="text-white">físicamente en la misma vuelta</span> donde marcó su mejor tiempo. Mostramos el lap# arriba de cada fila.
                      </>
                    ) : (
                      <>
                        <Badge className="bg-violet-700/30 text-violet-300 border-violet-600/50 text-[10px] mr-1.5 align-middle">ÓPTIMO</Badge>
                        El mejor S1, mejor S2 y mejor S3 de <span className="text-white">cualquier vuelta</span> de cada piloto. Sumados dan su vuelta teórica. El lap# debajo indica de qué vuelta vino cada parcial.
                      </>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Sector Delta Table */}
                  <div className="mb-4 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-neutral-700">
                          <th className="text-left py-2 px-2 text-gray-400">Piloto</th>
                          {Array.from({ length: Math.max(...comparisonData.drivers.map(d => sectorsOf(d).length)) }, (_, i) => (
                            <th key={i} className="text-center py-2 px-2 text-gray-400">S{i + 1}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {comparisonData.drivers.map((driver, dIdx) => {
                          const driverSectors = sectorsOf(driver);
                          const numSectors = Math.max(...comparisonData.drivers.map(d => sectorsOf(d).length));
                          return (
                            <tr key={driver.driver_number} className="border-b border-neutral-800">
                              <td className="py-2 px-2">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getDriverColor(dIdx) }} />
                                  <span className="text-white text-xs">#{driver.driver_number}</span>
                                  {/* In 'real' mode, show which lap these sectors come from. */}
                                  {sectorMode === 'real' && driver.best_lap_number != null && (
                                    <Badge
                                      className="bg-blue-900/40 text-blue-300 border-blue-700/50 text-[9px] px-1 py-0"
                                      title={`Vuelta ${driver.best_lap_number} — la vuelta física donde se marcó la mejor`}
                                      data-testid={`real-lap-badge-${driver.driver_number}`}
                                    >
                                      L{driver.best_lap_number}
                                    </Badge>
                                  )}
                                </div>
                              </td>
                              {Array.from({ length: numSectors }, (_, sIdx) => {
                                const time = driverSectors[sIdx] || 0;
                                const allTimes = comparisonData.drivers.map(d => sectorsOf(d)[sIdx]).filter(t => t > 0);
                                const fastestTime = allTimes.length > 0 ? Math.min(...allTimes) : time;
                                const delta = time - fastestTime;
                                const isFastest = delta < 0.001 && time > 0;
                                const fromLap = sectorMode === 'optimal' && Array.isArray(driver.sector_best_laps)
                                  ? driver.sector_best_laps[sIdx]
                                  : null;

                                return (
                                  <td key={sIdx} className={`py-2 px-2 text-center ${isFastest ? 'bg-green-900/30' : ''}`}>
                                    {time > 0 ? (
                                      <div>
                                        <span className={`font-mono ${isFastest ? 'text-green-400 font-bold' : 'text-gray-300'}`}>
                                          {time.toFixed(3)}
                                        </span>
                                        {!isFastest && delta > 0 && (
                                          <span className="block text-xs text-red-400">+{delta.toFixed(3)}</span>
                                        )}
                                        {/* In 'optimal' mode, label each sector with the lap it came from. */}
                                        {fromLap > 0 && (
                                          <span
                                            className="block text-[9px] text-violet-400/80 font-mono mt-0.5"
                                            title={`Mejor S${sIdx + 1} marcado en la vuelta ${fromLap}`}
                                            data-testid={`sector-from-lap-${driver.driver_number}-${sIdx}`}
                                          >
                                            L{fromLap}
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-gray-600">-</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={getSectorChartData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                      <XAxis dataKey="sector" stroke="#9ca3af" />
                      <YAxis 
                        stroke="#9ca3af"
                        domain={getYAxisDomain(
                          getSectorChartData(), 
                          comparisonData.drivers.map(d => d.driver_name)
                        )}
                        tickFormatter={(value) => value.toFixed(2)}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#171717',
                          border: '1px solid #7f1d1d',
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: '#ffffff' }}
                        formatter={(value, name) => {
                          const allValues = comparisonData.drivers.map(d => {
                            const sectorNum = parseInt(name.replace('S', '')) || 0;
                            return d.sector_bests[sectorNum - 1] || Infinity;
                          });
                          const fastest = Math.min(...allValues.filter(v => v < Infinity));
                          const delta = value - fastest;
                          if (delta > 0.001) {
                            return [`${value.toFixed(3)}s (+${delta.toFixed(3)})`, name];
                          }
                          return [`${value.toFixed(3)}s ⚡`, name];
                        }}
                      />
                      <Legend />
                      {comparisonData.drivers.map((driver, idx) => (
                        <Line
                          key={driver.driver_number}
                          type="monotone"
                          dataKey={driver.driver_name}
                          stroke={getDriverColor(idx)}
                          strokeWidth={3}
                          dot={{ fill: getDriverColor(idx), r: 5 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Radar Chart */}
              <Card className="bg-neutral-900 border-red-900/30">
                <CardHeader>
                  <CardTitle className="text-white">Performance Radar</CardTitle>
                  <CardDescription className="text-gray-400">
                    Comparación de rendimiento por sector (mayor = mejor)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <RadarChart data={getRadarChartData()}>
                      <PolarGrid stroke="#262626" />
                      <PolarAngleAxis dataKey="sector" stroke="#9ca3af" />
                      <PolarRadiusAxis stroke="#9ca3af" domain={[0, 100]} angle={90} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#171717',
                          border: '1px solid #7f1d1d',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      {comparisonData.drivers.map((driver, idx) => (
                        <Radar
                          key={driver.driver_number}
                          name={driver.driver_name}
                          dataKey={driver.driver_name}
                          stroke={getDriverColor(idx)}
                          fill={getDriverColor(idx)}
                          fillOpacity={0.3}
                        />
                      ))}
                    </RadarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Delta Table */}
              {comparisonData.deltas && comparisonData.deltas.length > 0 && (
                <Card className="bg-neutral-900 border-red-900/30">
                  <CardHeader>
                    <CardTitle className="text-white">Deltas por Sector</CardTitle>
                    <CardDescription className="text-gray-400">
                      Diferencias de tiempo entre pilotos
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-red-900/30">
                            <th className="text-left py-3 px-4 text-gray-300 font-semibold">Sector</th>
                            {comparisonData.drivers.slice(0, 2).map((driver, idx) => (
                              <th
                                key={driver.driver_number}
                                className="text-center py-3 px-4 text-gray-300 font-semibold"
                              >
                                #{driver.driver_number}
                              </th>
                            ))}
                            <th className="text-center py-3 px-4 text-gray-300 font-semibold">Delta</th>
                          </tr>
                        </thead>
                        <tbody>
                          {comparisonData.deltas.map((delta, sectorIdx) => (
                            <tr
                              key={sectorIdx}
                              className="border-b border-neutral-800 hover:bg-slate-800/50"
                              data-testid={`delta-row-s${delta.sector}`}
                            >
                              <td className="py-3 px-4 font-medium text-white">
                                S{delta.sector}
                              </td>
                              <td className="text-center py-3 px-4 text-gray-300">
                                {formatTime(delta.driver1_time)}
                              </td>
                              <td className="text-center py-3 px-4 text-gray-300">
                                {formatTime(delta.driver2_time)}
                              </td>
                              <td className="text-center py-3 px-4">
                                <div className="flex items-center justify-center gap-2">
                                  {delta.faster_driver === comparisonData.drivers[0].driver_number ? (
                                    <TrendingDown className="w-4 h-4 text-green-400" />
                                  ) : (
                                    <TrendingUp className="w-4 h-4 text-red-400" />
                                  )}
                                  <span
                                    className={`font-mono font-semibold ${
                                      delta.faster_driver === comparisonData.drivers[0].driver_number
                                        ? 'text-green-400'
                                        : 'text-red-400'
                                    }`}
                                  >
                                    {delta.delta.toFixed(3)}s
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RaceComparison;
